// Auth API Layer
// Endpoints come from platform constants (auth predates domain registry codegen).

import { APIEndpoints, AuthEvents } from "src/platform/constants";
import { apiBaseUrls } from "src/platform/constants/shared-constants";
import { EventBus } from "src/platform/events/EventBus";
import API from "src/platform/http/shared-http";
import debugLogger from "src/platform/utils/debugLogger";
import { clearAllRushaStorage } from "src/platform/utils/storage";

import { User, LoginCredentials } from "../types/auth.types";

import type { IAuthApi } from "./IAuthApi";

export interface AuthLoginResponse {
  token: string;
  user: User;
}

export class AuthApi implements IAuthApi {
  private eventBus: EventBus;

  constructor(eventBus: EventBus) {
    this.eventBus = eventBus;
  }

  async login(credentials: LoginCredentials): Promise<AuthLoginResponse> {
    try {
      debugLogger.info("auth-api", "Logging in", { email: credentials.email });

      const api = new API(apiBaseUrls.BACKEND, APIEndpoints.AUTH_LOGIN);
      const response = await api.post<AuthLoginResponse>(credentials);

      this.eventBus.emitGlobal(AuthEvents.LOGIN_SUCCESS, {
        user: response.user,
        token: response.token,
      });

      debugLogger.info("auth-api", "Login successful");
      return response;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Login failed";
      debugLogger.error("auth-api", "Login failed", error);

      this.eventBus.emitGlobal(AuthEvents.LOGIN_FAILED, {
        error: errorMessage,
      });

      throw error;
    }
  }

  async logout(): Promise<void> {
    debugLogger.info("auth-api", "Logging out");
    clearAllRushaStorage();
    this.eventBus.emitGlobal(AuthEvents.LOGOUT_SUCCESS, {});
    debugLogger.info("auth-api", "Logout complete");
  }

  async fetchCurrentUser(token: string): Promise<User> {
    try {
      debugLogger.info("auth-api", "Fetching current user");

      const api = new API(apiBaseUrls.BACKEND, APIEndpoints.AUTH_ME);
      const response = await api.get<{ user: User }>();

      this.eventBus.emitGlobal(AuthEvents.LOGIN_SUCCESS, {
        user: response.user,
        token,
      });

      debugLogger.info("auth-api", "Current user fetched");
      return response.user;
    } catch (error) {
      debugLogger.error("auth-api", "Failed to fetch current user", error);
      throw error;
    }
  }
}
